export type TranslationKey = keyof typeof translations['en']

export const translations = {
  en: {
    app_title: 'CircuitAI',
    start_design: 'Start Designing',
    hero_subtitle: 'AI-powered circuit design platform that makes complex designs simple.',
    features_ai: 'AI Design',
    features_ai_desc: 'Describe your needs in natural language, AI generates circuit plans',
    features_tools: 'Professional Tools',
    features_tools_desc: 'Complete toolchain and component library for circuit design',
    features_fast: 'Rapid Prototyping',
    features_fast_desc: 'Quickly go from concept to practical prototypes',
    ready_to_start: 'Ready to start your circuit journey?',
    cta_subtitle: 'Whether you are a pro engineer or a hobbyist, CircuitAI helps you move faster',
    try_now: 'Try Now',
    ai_assistant: 'AI Design Assistant',
    export_chat: 'Export',
    clear_chat: 'Clear',
    api_settings: 'API Settings',
    requirements_card: 'Requirements',
    generate_draft: 'Generate Draft',
    input_placeholder: 'Describe your circuit design needs...',
    quick_start: 'Quick start',
    typing_hint: 'Organizing your circuit plan...',
    send: 'Send',
    settings_title: 'AI API Settings',
    cancel: 'Cancel',
    test_connection: 'Test Connection',
    save_config: 'Save',
    provider_label: 'AI Provider',
    api_key_label: 'API Key',
    api_url_label: 'API URL',
    model_name_label: 'Model Name',
    advanced_options: 'Advanced Options',
    request_format: 'Request Format',
    response_format: 'Response Format',
    custom_headers: 'Custom Headers',
    add: 'Add',
    delete: 'Delete',
    assistant_welcome: 'Hello! I\'m the CircuitAI assistant. I can help you:\n\n🔹 Analyze requirements\n🔹 Generate circuit schematics\n🔹 Recommend components\n🔹 Calculate circuit parameters\n\nPlease describe what you want to design!',
    quick_action_5v_regulator: 'Design a 5V regulator circuit',
    quick_action_led_blink: 'Design an LED blinking circuit',
    quick_action_opamp_amplifier: 'Need an op-amp amplifier',
    quick_action_audio_amp: 'Design an audio power amplifier',

    req_card_title: 'Requirements',
    reset: 'Reset',
    field_circuit_type: 'Circuit Type',
    field_input_power: 'Input Power',
    field_output_target: 'Target Output',
    field_load: 'Load',
    field_priorities: 'Priorities',
    field_environment: 'Environment / Compliance',
    field_preferences: 'Preferences / Exclusions',
    field_acceptance_criteria: 'Acceptance Criteria',
    confirmed_items: 'Confirmed',
    suggestions_title: 'Next Suggestions',
    risks_title: 'Risks',
    changes_title: 'Change Log',
    back_home: 'Back to Home',
    current_project: 'Current Project',
    project_manager: 'Project Manager',
    save_project: 'Save Project',
    tab_ascii: 'ASCII Schematic',
    tab_visual: 'Visual Schematic',
    components: 'components',
    theme_light: 'Light',
    theme_dark: 'Dark',
    toggle_grid: 'Toggle grid',
    toggle_labels: 'Toggle labels',
    fullscreen: 'Fullscreen',
    export_svg: 'SVG',
    export_png: 'PNG',
    grid_size: 'Grid size',
    show_labels: 'Show labels',
    show_pin_numbers: 'Show pin numbers',
    edit_mode: 'Edit mode',
    generating_schematic: 'Generating schematic...',
    visual_schematic_hint: 'AI will generate a visual schematic from the chat',
    component_library: 'Component Library',
    selected_symbol: 'Selected {name}',
    canvas_size: 'Canvas',
    scale_label: 'Scale',
    grid_label: 'Grid',
    theme_label: 'Theme',
    status_on: 'On',
    status_off: 'Off',
    bom_title: 'Bill of Materials (BOM)',
    stats: 'Stats',
    group: 'Group',
    export_csv: 'Export CSV',
    export_pdf: 'Export PDF',
    no_bom: 'No BOM data',
    bom_hint: 'AI will auto-generate the bill of materials based on the design',
    no_schematic: 'No schematic yet',
    schematic_hint: 'Please describe your circuit design needs in the chat on the left',
    kinds_of_components: 'Kinds',
    total_quantity: 'Total Quantity',
    estimated_cost: 'Estimated Cost',
    component_categories: 'Categories',
    table_index: '#',
    table_ref: 'Ref',
    table_component: 'Component',
    table_value: 'Value',
    table_package: 'Package',
    table_qty: 'Qty',
    table_manufacturer: 'Manufacturer',
    table_partnumber: 'Part Number',
    table_supplier: 'Supplier',
    table_price: 'Price (¥)',
    table_subtotal: 'Subtotal (¥)',
    actions: 'Actions',
    cancel_text: 'Cancel',
    saved_success: 'Saved successfully',
    no_data_to_export: 'No data to export',
    exported_bom_csv: 'BOM CSV exported',
    pdf_export_wip: 'PDF export is under development...',
    purchasing_lookup: 'Looking up purchasing info for {name}...',
    show_total_range: 'Showing {start}-{end} of {total} records',
    total_cost_estimate: 'Total Cost Estimate',
    price_disclaimer: '* Prices are reference only and may vary in purchasing',
    // CircuitViewer
    copied: 'Copied to clipboard',
    copy_failed: 'Copy failed',
    line_numbers: 'Line #',
    copy: 'Copy',
    download: 'Download',
    description: 'Description',
    no_component_info: 'No component info',
    type: 'Type',
    circuit_properties: 'Circuit Properties',
    no_connection_info: 'No connection info',
    circuit_design: 'Circuit Design',
    schematic_tab: 'Schematic',
    components_tab: 'Components',
    connections_tab: 'Connections',
  },
  zh: {
    app_title: 'CircuitAI',
    start_design: '开始设计',
    hero_subtitle: '基于人工智能的智能电路设计平台，让复杂的电路设计变得简单易懂',
    features_ai: 'AI智能设计',
    features_ai_desc: '通过自然语言描述需求，AI自动生成电路方案',
    features_tools: '专业工具',
    features_tools_desc: '提供完整的电路设计工具链和元件库',
    features_fast: '快速原型',
    features_fast_desc: '快速从概念到可实现的电路原型设计',
    ready_to_start: '准备开始您的电路设计之旅？',
    cta_subtitle: '无论您是专业工程师还是电子爱好者，CircuitAI都能帮助您快速实现电路设计目标',
    try_now: '立即体验',
    ai_assistant: 'AI设计助手',
    export_chat: '导出对话',
    clear_chat: '清空对话',
    api_settings: 'API设置',
    requirements_card: '需求卡片',
    generate_draft: '生成草案',
    input_placeholder: '描述您的电路设计需求...',
    quick_start: '快速开始',
    typing_hint: '正在整理电路方案...',
    send: '发送',
    settings_title: 'AI API配置',
    cancel: '取消',
    test_connection: '测试连接',
    save_config: '保存配置',
    provider_label: 'AI提供商',
    api_key_label: 'API密钥',
    api_url_label: 'API地址',
    model_name_label: '模型名称',
    advanced_options: '高级选项',
    request_format: '请求格式',
    response_format: '响应格式',
    custom_headers: '自定义请求头',
    add: '添加',
    delete: '删除',
    assistant_welcome: '您好！我是CircuitsAI智能助手。我可以帮您：\n\n🔹 分析电路设计需求\n🔹 生成电路原理图\n🔹 提供元件选型建议\n🔹 计算电路参数\n\n请详细描述您想要设计的电路功能！',
    quick_action_5v_regulator: '设计一个5V稳压电路',
    quick_action_led_blink: '帮我设计LED闪烁电路',
    quick_action_opamp_amplifier: '需要一个运放放大器',
    quick_action_audio_amp: '设计音频功放电路',

    req_card_title: '需求卡片',
    reset: '重置',
    field_circuit_type: '电路类型',
    field_input_power: '输入电源',
    field_output_target: '目标输出',
    field_load: '负载特性',
    field_priorities: '优先级（多选）',
    field_environment: '环境/安规',
    field_preferences: '元件偏好/禁用',
    field_acceptance_criteria: '验收标准',
    confirmed_items: '已确认项',
    suggestions_title: '下一步建议',
    risks_title: '风险清单',
    changes_title: '变更单',
    back_home: '返回首页',
    current_project: '当前项目',
    project_manager: '项目管理',
    save_project: '保存项目',
    tab_ascii: 'ASCII电路图',
    tab_visual: '可视化电路图',
    components: '个元件',
    theme_light: '浅色',
    theme_dark: '深色',
    toggle_grid: '切换网格',
    toggle_labels: '切换标签',
    fullscreen: '全屏',
    export_svg: 'SVG',
    export_png: 'PNG',
    grid_size: '网格大小',
    show_labels: '显示标签',
    show_pin_numbers: '显示引脚编号',
    edit_mode: '编辑模式',
    generating_schematic: '正在生成电路图...',
    visual_schematic_hint: 'AI 将根据聊天内容生成可视化电路图',
    component_library: '组件库',
    selected_symbol: '选择了 {name}',
    canvas_size: '画布大小',
    scale_label: '缩放',
    grid_label: '网格',
    theme_label: '主题',
    status_on: '开启',
    status_off: '关闭',
    bom_title: '物料清单 (BOM)',
    stats: '统计',
    group: '分组',
    export_csv: '导出CSV',
    export_pdf: '导出PDF',
    no_bom: '暂无BOM数据',
    bom_hint: 'AI将根据电路设计自动生成物料清单',
    no_schematic: '暂无电路图',
    schematic_hint: '请在左侧描述您的电路设计需求',
    kinds_of_components: '种元件',
    total_quantity: '总数量',
    estimated_cost: '预估成本',
    component_categories: '元件类别',
    table_index: '序号',
    table_ref: '标号',
    table_component: '元件名称',
    table_value: '参数值',
    table_package: '封装',
    table_qty: '数量',
    table_manufacturer: '制造商',
    table_partnumber: '型号',
    table_supplier: '供应商',
    table_price: '单价 (¥)',
    table_subtotal: '小计 (¥)',
    actions: '操作',
    cancel_text: '取消',
    saved_success: '保存成功',
    no_data_to_export: '暂无数据可导出',
    exported_bom_csv: 'BOM表格已导出',
    pdf_export_wip: 'PDF导出功能开发中...',
    purchasing_lookup: '正在查找 {name} 的采购信息...',
    show_total_range: '显示 {start}-{end} 条，共 {total} 条记录',
    total_cost_estimate: '总成本估算',
    price_disclaimer: '* 价格仅供参考，实际采购价格可能有所不同',
    // CircuitViewer
    copied: '已复制到剪贴板',
    copy_failed: '复制失败',
    line_numbers: '行号',
    copy: '复制',
    download: '下载',
    description: '电路说明',
    no_component_info: '暂无元件信息',
    type: '类型',
    circuit_properties: '整体电路参数',
    no_connection_info: '暂无连接信息',
    circuit_design: '电路设计',
    schematic_tab: '电路图',
    components_tab: '元件',
    connections_tab: '连接',
  }
}


